import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Inject, OnDestroy, Output, PLATFORM_ID, Renderer2, RendererStyleFlags2, ViewChild } from '@angular/core';
import { Router, RouterLink, NavigationEnd, NavigationStart } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { AboutusComponent } from '../aboutus/aboutus.component';
import { ResourcesNavcardComponent } from '../resources-navcard/resources-navcard.component';
import { CareersNavcardComponent } from '../careers-navcard/careers-navcard.component';
import { CapabilitiesNavcardComponent } from '../capabilities-navcard/capabilities-navcard.component';
import { SolutionsNavcardComponent } from '../solutions-navcard/solutions-navcard.component';
import { GalleryNavcardComponent } from '../gallery-navcard/gallery-navcard.component';
import { ContactModalService } from '../../services/contact-modal.service';
import type { Offcanvas as OffcanvasType } from 'bootstrap';

// Bootstrap's JS has import-time side effects (e.g. Modal's dismiss-trigger
// listener touches `document` as soon as the module is evaluated), which
// crashes SSR. Load it lazily, browser-only, instead of a static import.

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgbDropdownModule,
    AboutusComponent,
    ResourcesNavcardComponent,
    CareersNavcardComponent,
    CapabilitiesNavcardComponent,
    SolutionsNavcardComponent,
    GalleryNavcardComponent
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss'
})
export class NavbarComponent implements AfterViewInit, OnDestroy {

  @Output() contactClick = new EventEmitter<void>();

  @ViewChild('offcanvasElement') offcanvasRef!: ElementRef;

  isCollapsed: boolean = true;
  showAboutUs: boolean = false;
  showResources: boolean = false;
  showCareers: boolean = false;
  /** "Capabilities" mega menu — services and industries. */
  showCapabilities: boolean = false;
  /** "Solutions" mega menu — the nine Kalpita products. */
  showSolutions: boolean = false;
  showGallery: boolean = false;

  isScrolled = false;

  private routerSubscription?: Subscription;
  private offcanvasCtor?: typeof OffcanvasType;
  private readonly isBrowser: boolean;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    this.isScrolled = window.pageYOffset > 10;
  }

  constructor(
    public router: Router,
    private el: ElementRef,
    private renderer: Renderer2,
    private contactModal: ContactModalService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  private async getOffcanvas(): Promise<typeof OffcanvasType | undefined> {
    if (!this.isBrowser) return undefined;
    if (!this.offcanvasCtor) {
      const bootstrap = await import('bootstrap');
      this.offcanvasCtor = bootstrap.Offcanvas;
    }
    return this.offcanvasCtor;
  }

  ngAfterViewInit(): void {
    if (typeof document !== 'undefined') {
      const nav = this.el.nativeElement.querySelector('.main-navbar');
      if (nav) {
        const h = (typeof nav.getBoundingClientRect === 'function' ? (typeof nav.getBoundingClientRect === 'function' ? (typeof nav.getBoundingClientRect === 'function' ? nav.getBoundingClientRect() : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}).height;
        this.renderer.setStyle(
          document.documentElement, '--nav-height', `${Math.ceil(h)}px`,
          RendererStyleFlags2.DashCase
        );
      }

      const btn = this.el.nativeElement.querySelector('.contact-btn') as HTMLElement;
      if (btn) {
        btn.addEventListener('mousemove', (e: MouseEvent) => {
          const rect = (typeof btn.getBoundingClientRect === 'function' ? (typeof btn.getBoundingClientRect === 'function' ? (typeof btn.getBoundingClientRect === 'function' ? btn.getBoundingClientRect() : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0});
          const x = ((e.clientX - rect.left) / rect.width) * 100 + '%';
          const y = ((e.clientY - rect.top) / rect.height) * 100 + '%';

          btn.style.setProperty('--mx', x);
          btn.style.setProperty('--my', y);
        });
      }
    }
  }

  openContactModal() {
    this.contactModal.open();

    // Close all dropdowns
    this.closeAllDropdowns();
  }

  nav(route: string) {
    // Close mobile offcanvas first
    this.closeOffcanvas();

    // Close all mega-menu dropdowns
    this.closeAllDropdowns();

    if (route === '/') {
      if (this.router.url === '/') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        this.router.navigate(['/']).then(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        });
      }
    } else {
      this.router.navigate([`/${route}`]);
    }
  }

  private async closeOffcanvas() {
    if (!this.offcanvasRef?.nativeElement) return;

    const Offcanvas = await this.getOffcanvas();
    if (!Offcanvas) return;

    const offcanvasEl = this.offcanvasRef.nativeElement;
    const bsOffcanvas = Offcanvas.getInstance(offcanvasEl);

    if (bsOffcanvas) {
      bsOffcanvas.hide();

      // Use requestAnimationFrame to ensure the hide() has been processed
      requestAnimationFrame(() => {
        // Remove all backdrop elements immediately
        const backdrops = document.querySelectorAll('.offcanvas-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());

        // Restore body styles
        document.body.classList.remove('modal-open');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');

        // Dispose the offcanvas instance to prevent it from recreating backdrop
        bsOffcanvas.dispose();
      });
    }
  }

  private closeAllDropdowns() {
    this.showAboutUs = false;
    this.showResources = false;
    this.showCareers = false;
    this.showCapabilities = false;
    this.showSolutions = false;
    this.showGallery = false;
  }

  toggleCareers() {
    this.showCareers = !this.showCareers;
    if (this.showCareers) {
      this.closeOtherDropdowns('careers');
    }
  }

  toggleResources() {
    this.showResources = !this.showResources;
    if (this.showResources) {
      this.closeOtherDropdowns('resources');
    }
  }

  toggleAboutUs() {
    this.showAboutUs = !this.showAboutUs;
    if (this.showAboutUs) {
      this.closeOtherDropdowns('aboutus');
    }
  }

  toggleCapabilities() {
    this.showCapabilities = !this.showCapabilities;
    if (this.showCapabilities) {
      this.closeOtherDropdowns('capabilities');
    }
  }

  toggleSolutions() {
    this.showSolutions = !this.showSolutions;
    if (this.showSolutions) {
      this.closeOtherDropdowns('solutions');
    }
  }

  toggleGallery() {
    this.showGallery = !this.showGallery;
    if (this.showGallery) {
      this.closeOtherDropdowns('gallery');
    }
  }

  private closeOtherDropdowns(current: string) {
    if (current !== 'capabilities') this.showCapabilities = false;
    if (current !== 'solutions') this.showSolutions = false;
    if (current !== 'resources') this.showResources = false;
    if (current !== 'careers') this.showCareers = false;
    if (current !== 'gallery') this.showGallery = false;
    if (current !== 'aboutus') this.showAboutUs = false;
  }

  ngOnInit() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateNavHeight.bind(this));
    }

    // Subscribe to router events to close menu on navigation
    // Use NavigationStart to close dropdowns immediately (prevents them showing on new page)
    // Use NavigationEnd to clean up offcanvas backdrop after navigation completes
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd))
      .subscribe((event) => {
        if (event instanceof NavigationStart) {
          // Close dropdowns immediately when navigation starts
          this.closeAllDropdowns();
        } else if (event instanceof NavigationEnd) {
          // Close offcanvas and clean up backdrop after navigation completes
          this.closeOffcanvas();
        }
      });
  }

  /**
   * Publishes the measured navbar height as `--nav-height` on <html>.
   *
   * `Renderer2.setStyle` silently drops custom properties unless
   * `RendererStyleFlags2.DashCase` is passed -- without it this was a no-op and
   * the variable never existed. Consumers must still supply a fallback
   * (`var(--nav-height, 96px)`) because the value only appears once the browser
   * has measured; prerendered HTML and the first paint have neither.
   *
   * Layout-critical clearance (the blog and product hero top padding) stays on
   * the hard-coded 96px so it cannot shift between server and client render;
   * this variable is for scroll-anchor offsets, where being a few pixels out is
   * harmless.
   */
  updateNavHeight() {
    if (typeof document !== 'undefined') {
      const nav = this.el.nativeElement.querySelector('.main-navbar');
      if (nav) {
        const h = (typeof nav.getBoundingClientRect === 'function' ? (typeof nav.getBoundingClientRect === 'function' ? (typeof nav.getBoundingClientRect === 'function' ? nav.getBoundingClientRect() : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}) : {top:0,left:0,bottom:0,right:0,width:0,height:0,x:0,y:0}).height;
        this.renderer.setStyle(
          document.documentElement, '--nav-height', `${Math.ceil(h)}px`,
          RendererStyleFlags2.DashCase
        );
      }
    }
  }

  ngOnDestroy() {
    // Clean up router subscription to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
}